import * as React from 'react';

import { cn } from '../../../lib/utils';

// ─── Constants ──────────────────────────────────────────────────────────────

const VARIANT_CLASSES = {
  danger: 'bg-destructive/15 text-destructive',
  info: 'bg-info/15 text-info',
  neutral: 'bg-primary/10 text-primary',
  success: 'bg-success/15 text-success',
} as const;

// ─── Types ────────────────────────────────────────────────────────────────────

export type MetricTileVariant = keyof typeof VARIANT_CLASSES;

export interface MetricTileProps {
  /** Icon rendered inside the tinted chip — pass a sized Lucide icon element. */
  icon: React.ReactNode;
  /** Translated label — i18n stays app-side, callers pass the resolved string. */
  label: string;
  value: number | string;
  /** Controls the icon chip's tint. */
  variant: MetricTileVariant;
}

// ─── Component ────────────────────────────────────────────────────────────────

/** Compact metric tile — a tinted icon chip next to a label/value pair. Used to build KPI rows (e.g. the import report). */
export function MetricTile({ icon, label, value, variant }: MetricTileProps): React.JSX.Element {
  return (
    <div className="flex items-center gap-3 rounded-lg bg-muted/40 p-3">
      <div className={cn('rounded-lg p-2', VARIANT_CLASSES[variant])}>{icon}</div>
      <div className="min-w-0">
        <p className="truncate text-xs font-medium text-muted-foreground">{label}</p>
        <p className="text-xl font-bold leading-tight text-foreground">{value}</p>
      </div>
    </div>
  );
}
